import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useMarketingT } from "@/hooks/useMarketingT";
import { SEO } from "@/components/SEO";
import { CheckCircle2, Sparkles } from "lucide-react";
import invoicesShowcaseAsset from "@/assets/sida-invoices-showcase.webp.asset.json";
import invoicesShowcaseEnAsset from "@/assets/sida-invoices-showcase-en.webp.asset.json";
import aiAdvisorAnimationArAsset from "@/assets/sida-advisor-ar.webp.asset.json";
import aiAdvisorAnimationEnAsset from "@/assets/sida-advisor-en.webp.asset.json";
import { SpeedCarousel } from "@/components/marketing/SpeedCarousel";
import { MultiCompanyIncomeStatement } from "@/components/marketing/MultiCompanyIncomeStatement";
import { LocalizedFinancialHealth } from "@/components/marketing/LocalizedFinancialHealth";
import { UrduDashboardPreview } from "@/components/marketing/UrduDashboardPreview";
import { HeroCinematic } from "@/components/marketing/HeroCinematic";


const Home = () => {
  const { t, lang } = useMarketingT();
  const hp = (_key: string, fb: string) => fb;

  return (
    <>
      <SEO
        path="/"
        title={t(
          "برنامج سيدا المحاسبي | نظام إدارة الحسابات والفروع الذكي",
          "Sida Accounting App | Smart Books, Branches & ZATCA Compliance",
          "Sida Comptabilité | Gestion intelligente des comptes et succursales",
        )}
        description={t(
          "اكتشف نظام سيدا (Sida) المحاسبي المبتكر لإدارة القيود المالية، المصروفات، وتوزيع تكاليف الفروع آلياً. النظام الأسهل والأقوى لإدارة حسابات الشركات والمقاهي في السعودية ومصر.",
          "Discover Sida (سيدا) — the smart cloud accounting app to manage journals, expenses, and multi-branch cost allocation automatically. The easiest, most powerful books for companies and cafés in Saudi Arabia and Egypt.",
          "Découvrez Sida (سيدا) — l'application comptable cloud intelligente pour gérer écritures, dépenses et répartition multi-succursales. La solution la plus simple et puissante pour les entreprises et cafés en Arabie saoudite et en Égypte.",
        )}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "سيدا المحاسبي", // i18n-allow: brand name in SEO JSON-LD
            alternateName: ["Sida Accounting App", "SIDA", "سيدا", "برنامج سيدا", "برنامج سيدا المحاسبي"], // i18n-allow: brand aliases
            url: "https://sida.one/",
            logo: "https://sida.one/sida-mark.svg",
            description: "سيدا (Sida) — نظام محاسبي سحابي متكامل لإدارة القيود المالية، المصروفات، الفروع، والتقارير المالية للشركات في السعودية ومصر.", // i18n-allow: SEO description (ar default for primary market)
          },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "سيدا المحاسبي", // i18n-allow: brand name
            alternateName: ["Sida Accounting App", "SIDA", "سيدا"], // i18n-allow: brand aliases
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            url: "https://sida.one/",
            description: t(
              "برنامج سيدا المحاسبي السحابي: متعدد الشركات، شجرة حسابات، قيود يومية، تقارير مالية، إدارة فروع، متوافق مع ZATCA.",
              "Sida cloud accounting: multi-company, chart of accounts, journal entries, financial reports, branch management, ZATCA-compliant.",
              "Sida comptabilité cloud: multi-entreprises, plan comptable, écritures, rapports, multi-succursales, conforme ZATCA.",
            ),
            inLanguage: ["ar", "en", "fr"],
            offers: { "@type": "Offer", price: "0", priceCurrency: "SAR" },
          },
        ]}
      />


      <HeroCinematic />

      <section className="container mx-auto px-4 pt-16 pb-4">
        <h2 className="font-display text-3xl md:text-5xl text-center">
          <span className="bg-gradient-to-r from-[#5BC75A] via-[hsl(207_74%_42%)] to-[hsl(192_88%_50%)] bg-clip-text text-transparent">
            {t("كل فاتورة جاهزة للعميل وللنظام", "Every Invoice Ready to Send and Stay Compliant", "Des factures prêtes à envoyer et conformes aux exigences")}
          </span>
        </h2>
        <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-[#5BC75A] to-[hsl(192_88%_50%)]" />
      </section>

      <section className="relative overflow-hidden py-16 md:py-20 bg-background">
        <div className="pointer-events-none absolute inset-0 -z-0 opacity-60" style={{ background: "radial-gradient(50% 40% at 90% 10%, hsl(192 88% 50% / 0.08), transparent 60%), radial-gradient(40% 40% at 5% 95%, hsl(142 60% 45% / 0.07), transparent 60%)" }} />
        <div className="container relative mx-auto px-4">
          <div className="grid gap-8 lg:gap-12 lg:grid-cols-5 items-center">
            <div className="text-center lg:text-start lg:col-span-2">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed whitespace-pre-wrap max-w-xl mx-auto lg:mx-0">
                {t(
                  "أنشئ الفواتير الإلكترونية خلال ثوانٍ، متوافقة مع متطلبات هيئة الزكاة والضريبة (ZATCA)، وأرسلها مباشرة عبر واتساب أو البريد الإلكتروني.",
                  "Create e-invoices in seconds, compliant with ZATCA requirements, and send them directly via WhatsApp or email.",
                  "Créez des factures électroniques en quelques secondes, conformes aux exigences de la ZATCA, et envoyez-les directement via WhatsApp ou e-mail."
                )}
              </p>
            </div>
            <div className="relative lg:col-span-3 lg:-mx-6 xl:-mx-12">
              <img
                src={lang === "ar" ? invoicesShowcaseAsset.url : invoicesShowcaseEnAsset.url}
                alt={t("فواتير سيدا الإلكترونية", "SIDA e-invoices", "Factures électroniques SIDA")}
                loading="lazy"
                decoding="async"
                width={1600}
                height={lang === "ar" ? 893 : 762}
                sizes="(max-width: 1024px) 100vw, 60vw"
                className="w-full h-auto object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-16 md:py-20 bg-muted/30">
        <div className="pointer-events-none absolute inset-0 -z-0 opacity-60" style={{ background: "radial-gradient(45% 40% at 10% 10%, hsl(207 74% 42% / 0.08), transparent 60%), radial-gradient(40% 40% at 95% 90%, hsl(192 88% 50% / 0.08), transparent 60%)" }} />
        <div className="container relative mx-auto px-4">
          <div className="grid gap-8 lg:gap-12 lg:grid-cols-2 items-center">
            <div className="text-center lg:text-start">
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                <span className="bg-gradient-to-r from-[hsl(207_74%_42%)] via-[hsl(192_88%_50%)] to-[#5BC75A] bg-clip-text text-transparent">
                  {t("لا تقرأ التقارير فقط… اسألها", "Don’t just read your reports… ask them.", "Ne vous contentez pas de lire vos rapports… interrogez-les.")}
                </span>
              </h2>
              <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed whitespace-pre-wrap max-w-xl mx-auto lg:mx-0">
                {t("اربط حساب الذكاء الاصطناعي الخاص بك مع “المستشار الذكي”، واسأل سيدا عن أرقامك بدلًا من البحث داخل الجداول.", "Connect your AI account to the Smart Advisor, and ask SIDA about your numbers instead of searching through spreadsheets.", "Connectez votre compte d’intelligence artificielle au Conseiller Intelligent, et interrogez SIDA sur vos chiffres au lieu de chercher dans des tableaux.")}
              </p>
            </div>
            <div className="relative aspect-[1200/1005]">
              {lang === "ar" ? (
                <img src={aiAdvisorAnimationArAsset.url} alt={t("المستشار الذكي", "AI Smart Advisor", "Conseiller intelligent IA")} loading="lazy" decoding="async" width={1200} height={1005} sizes="(max-width: 1024px) 100vw, 50vw" className="h-full w-full object-contain drop-shadow-2xl rounded-xl" />
              ) : (
                <img src={aiAdvisorAnimationEnAsset.url} alt={t("المستشار الذكي", "AI Smart Advisor", "Conseiller intelligent IA")} loading="lazy" decoding="async" width={1200} height={1005} sizes="(max-width: 1024px) 100vw, 50vw" className="h-full w-full object-contain drop-shadow-2xl rounded-xl" />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-16 md:py-20 bg-background">
        <div className="pointer-events-none absolute inset-0 -z-0 opacity-60" style={{ background: "radial-gradient(45% 40% at 10% 5%, hsl(207 74% 42% / 0.06), transparent 60%), radial-gradient(40% 40% at 95% 90%, hsl(192 88% 50% / 0.06), transparent 60%)" }} />
        <div className="container relative mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
              <span className="bg-gradient-to-r from-[hsl(207_74%_42%)] via-[hsl(192_88%_50%)] to-[#5BC75A] bg-clip-text text-transparent">
                {t("تابع الأداء، السيولة، وأشهر التشغيل المتاحة", "Track Your Performance, Liquidity, and Operating Runway", "Suivez vos performances, votre liquidité et votre autonomie d’exploitation")}
              </span>
            </h2>
            <div className="mx-auto mt-4 h-[3px] w-20 rounded-full bg-gradient-to-r from-[#5BC75A] to-[hsl(192_88%_50%)]" />
          </div>
          <p className="max-w-3xl mx-auto text-center text-foreground/90 whitespace-pre-wrap leading-relaxed">
            {t("سيدا يحول بيانات الفواتير والحسابات إلى مؤشرات واضحة تساعدك على فهم أداء الشركة، متابعة السيولة المتاحة، ومعرفة كم شهر يمكن أن تغطي مصروفاتك التشغيلية.", "SIDA turns your invoice and accounting data into clear indicators that help you understand business performance, track available liquidity, and know how many months your operating expenses can be covered.", "SIDA transforme les données de facturation et de comptabilité en indicateurs clairs qui vous aident à comprendre la performance de l’entreprise, suivre la liquidité disponible et savoir combien de mois vos charges d’exploitation peuvent être couvertes.")}
          </p>
          <div className="mx-auto mt-8 max-w-4xl">
            <LocalizedFinancialHealth />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-border bg-muted/30 py-16 md:py-20">
        <div className="container relative mx-auto px-4">
          <div className="grid items-center gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-14">
            <div className="text-center lg:text-start">
              <p className="mb-3 text-sm font-semibold text-primary">
                {t("لغة أقرب لفريقك", "A language your team knows", "Une langue familière à votre équipe")}
              </p>
              <h2 className="font-display text-3xl leading-tight md:text-4xl lg:text-5xl">
                <span className="bg-gradient-to-r from-[hsl(207_74%_42%)] via-[hsl(192_88%_50%)] to-[#5BC75A] bg-clip-text text-transparent">
                  {t("سيدا يدعم اللغة الأردية", "SIDA supports Urdu", "SIDA prend en charge l’ourdou")}
                </span>
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground lg:mx-0 md:text-lg">
                {t(
                  "استخدم واجهة سيدا بالأردية مع دعم كامل للكتابة من اليمين إلى اليسار، لتعمل فرقك بلغتها المفضلة دون تغيير طريقة العمل.",
                  "Use SIDA in Urdu with complete right-to-left support, so every team member can work comfortably in their preferred language.",
                  "Utilisez SIDA en ourdou avec une prise en charge complète de l’écriture de droite à gauche, pour que chaque membre travaille dans sa langue préférée.",
                )}
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2 lg:justify-start" aria-label={t("اللغات المتاحة", "Available languages", "Langues disponibles")}>
                {["العربية", "English", "Français", "اردو"].map((language) => ( // i18n-allow: language autonyms
                  <span key={language} className="rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground">
                    {language}
                  </span>
                ))}
              </div>
            </div>
            <UrduDashboardPreview />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-16 md:py-20 bg-muted/30">
        <div className="pointer-events-none absolute inset-0 -z-0 opacity-60" style={{ background: "radial-gradient(45% 40% at 90% 5%, hsl(207 74% 42% / 0.08), transparent 60%), radial-gradient(40% 40% at 5% 95%, hsl(192 88% 50% / 0.07), transparent 60%)" }} />
        <div className="container relative mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-6 md:mb-8">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
              <span className="bg-gradient-to-r from-[hsl(207_74%_42%)] via-[hsl(192_88%_50%)] to-[#5BC75A] bg-clip-text text-transparent">
                {t("أقل تكرارًا، أسرع إنجازًا", "Less Repetition. Faster Work.", "Moins de répétition, plus de rapidité")}
              </span>
            </h2>
            <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {t("سيدا يقلل الإدخال المتكرر ويساعدك على إنجاز القيود أسرع، من خلال تكرار الوصف تلقائيًا، وجدولة قيود الإيجارات والمصروفات المقدمة والمستحقة كمسودات شهرية جاهزة", "SIDA reduces repetitive data entry and helps you complete journal entries faster, with automatic description repetition and scheduled entries for rent, prepaid expenses, and accrued expenses as ready monthly drafts.", "SIDA réduit la saisie répétitive et vous aide à finaliser les écritures comptables plus rapidement, grâce à la répétition automatique des descriptions et à la planification des écritures de loyers, charges constatées d’avance et charges à payer sous forme de brouillons mensuels prêts à l’emploi.")}
            </p>
          </div>
          <SpeedCarousel />
          <div className="mt-10 max-w-3xl mx-auto text-center">
            <Button asChild size="lg">
              <Link to="/auth?mode=signup">{t("ابدأ تجربتك مجانا", "Start Free Trial", "Essai gratuit")}</Link>
            </Button>
            <p className="mt-6 text-foreground/90 whitespace-pre-wrap">
              {t("أدوات احترافية مصممة بعناية للمحاسبين ورواد الأعمال.", "Professional tools crafted for accountants and founders.", "Des outils professionnels pour comptables et entrepreneurs.")}
            </p>
          </div>
        </div>
      </section>

      {/* Multi-company income statement */}
      <section className="relative overflow-hidden py-16 md:py-20 bg-muted/30">
        <div
          className="pointer-events-none absolute inset-0 -z-0 opacity-60"
          style={{
            background:
              "radial-gradient(45% 40% at 10% 5%, hsl(207 74% 42% / 0.08), transparent 60%), radial-gradient(40% 40% at 95% 90%, hsl(192 88% 50% / 0.08), transparent 60%)",
          }}
        />
        <div className="container relative mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10 md:mb-14">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
              <span className="bg-gradient-to-r from-[hsl(207_74%_42%)] via-[hsl(192_88%_50%)] to-[#5BC75A] bg-clip-text text-transparent">
                {t(
                  "أدر كل شركاتك من مكان واحد",
                  "Manage all your companies from one place",
                  "Gérez toutes vos entreprises depuis un seul endroit",
                )}
              </span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              {t(
                "بدّل بين شركاتك بضغطة، واعرض قوائمها المالية مجمّعة لنظرة شاملة أو منفصلة لكل شركة على حدة.",
                "Switch between companies in one click, and view their financial statements consolidated for a full view or separated per company.",
                "Passez d'une entreprise à l'autre en un clic, et affichez leurs états financiers consolidés pour une vue d'ensemble ou séparés par entreprise.",
              )}
            </p>
            <div className="mx-auto mt-4 h-[3px] w-20 rounded-full bg-gradient-to-r from-[#5BC75A] to-[hsl(192_88%_50%)]" />
          </div>
          <MultiCompanyIncomeStatement />
        </div>
      </section>



      {/* CTA */}
      <section className="container mx-auto px-4 py-16">

        <div className="rounded-3xl p-10 md:p-16 text-center text-white" style={{ background: "var(--gradient-primary)" }}>
          <h2 className="font-display text-3xl md:text-4xl mb-4">
            {t("جاهز للبدء؟","Ready to get started?","Prêt à commencer ?")}
          </h2>
          <p className="opacity-90 mb-6 max-w-xl mx-auto">
            {t("أنشئ حساباً مجاناً خلال دقيقة واحدة وابدأ في إدارة محاسبة شركتك.","Create a free account in under a minute.","Créez un compte gratuit en moins d'une minute.")}
          </p>
          <Button asChild size="lg" variant="secondary">
            <Link to="/auth?mode=signup">{t("ابدأ الآن مجاناً","Start Free","Commencer")}</Link>
          </Button>
        </div>
      </section>
    </>
  );
};

export default Home;
